/**
 * Canonical Positioned-Section Model (Visualizer v2)
 * ==================================================
 *
 * The single source of truth for what product sits where on each wall/ceiling.
 * BOTH the wizard (via generators) and the 3D editor edit this model, and the
 * calculation engine consumes it. It replaces the old "stacked full-width band"
 * representation (SlatWallConfig.startHeight/numPanelRows + CoreDimensions
 * below/above-SW) as the input to costing.
 *
 * Branch-exclusive to `3d-visualizer-v3`. See docs/VISUALIZER-V2-PLAN.md.
 *
 * Coordinate system (per surface)
 * -------------------------------
 * Origin is the surface's BOTTOM-LEFT, units are FEET:
 *   x → rightward along the surface width (a wall's length / a ceiling's length)
 *   y → upward along the surface height (a wall's height / a ceiling's width)
 * This matches the BoardRect convention already used by room3d/boardLayoutEngine.
 *
 * Overlap & carving rules
 * -----------------------
 * Sections are classified as FILL or INSERT:
 *   - WCB sections are FILL.
 *   - SlatWall sections are INSERT.
 * Openings (windows and doors) are a third layer on top of both: they are not
 * sections, cost nothing themselves, and cut through whatever product is under
 * them — board and SlatWall alike.
 * Rules enforced by the editor and assumed by the resolver:
 *   - Two FILL sections never overlap.
 *   - Two INSERT sections never overlap.
 *   - An INSERT may sit within a FILL section and "carve" it: the FILL section's
 *     usable area = its rectangle MINUS every overlapping INSERT, decomposed into
 *     up to four sub-rectangles (the picture frame). This is computed at
 *     calc/render time by the resolver, NOT baked into stored geometry, so moving
 *     an INSERT just re-resolves.
 *   - Any region covered by no section is EMPTY (no product) → framing shows.
 */

import { ProductColor, PanelOrientation } from './index';
import type { SlatWallAccessoryKind } from '../data/slatwallAccessories';

/** Product that can occupy a section. */
export type SectionProductType = 'WCB' | 'SlatWall';

/** Resolution class — see "Overlap & carving rules" above. */
export type SectionFillMode = 'fill' | 'insert';

/** The four sides of a section, as seen in surface coordinates (top = higher y). */
export type EdgeSide = 'top' | 'right' | 'bottom' | 'left';

/** Which W&CB layout strategy the user picked for a section (default: fewest-joints). */
export type LayoutStrategy = 'fewest-joints' | 'lowest-price' | 'balanced' | 'least-waste';

/**
 * A positioned product section on a single surface. Rectangle in feet, in the
 * surface's bottom-left coordinate system described above.
 */
export interface Section {
  id: string;
  productType: SectionProductType;

  // Placement (feet, surface-local)
  x: number;
  y: number;
  width: number;
  height: number;

  color: ProductColor;

  /**
   * Panel orientation. W&CB only — SlatWall is always horizontal, so this is
   * ignored (and should be left undefined) for SlatWall sections.
   */
  orientation?: PanelOrientation;

  /**
   * Manual per-section length lock for the W&CB optimizer, in feet
   * (e.g. 8 | 10 | 12 | 14 | 16 | 20). null/undefined = automatic selection.
   * Lets a user restrict to lengths they can transport or source.
   */
  lengthLock?: number | null;

  /** W&CB layout strategy for this section. Undefined = the system default (fewest-joints). */
  layoutStrategy?: LayoutStrategy;

  /**
   * Trim chosen by the user for a side: a trim product's acronym ('JSnap', 'H',
   * 'Base', …) or 'none'. A side with no entry takes the default for whatever
   * it meets — see calculations/sections/trims.ts.
   */
  edges?: Partial<Record<EdgeSide, string>>;

  /**
   * SlatWall only: the accessories the user hung on this section (hooks,
   * baskets, shelves — data/slatwallAccessories.ts). They belong to the
   * section: they move, copy and go with it. Staging hangs its props on them.
   */
  accessories?: SlatWallAccessory[];
}

/**
 * An accessory on a SlatWall section, placed by the user. `x` and `y` are its
 * bottom-left corner FROM THE SECTION'S bottom-left (feet); its size comes from
 * its kind, and its top edge sits on a slot (3" pitch up the section).
 */
export interface SlatWallAccessory {
  id: string;
  kind: SlatWallAccessoryKind;
  x: number;
  y: number;
}

export type { SlatWallAccessoryKind } from '../data/slatwallAccessories';

/** What kind of hole in the wall an opening is. */
/**
 * A `'passage'` is not something a user adds: it is the cut left where a shared
 * wall between two rooms of a floor plan has been taken out (floor to just under
 * the lower ceiling). It is made and removed by the plan, and cannot be edited.
 */
export type OpeningKind = 'window' | 'door' | 'garageDoor' | 'passage';

/**
 * A window or door: a rectangle cut through the wall, in the same surface
 * coordinates as sections. Product is removed under it; trim goes around it.
 */
export interface Opening {
  id: string;
  kind: OpeningKind;
  x: number;
  y: number;
  width: number;
  height: number;
  /** Trim chosen by the user for a side — as `Section.edges`. */
  edges?: Partial<Record<EdgeSide, string>>;
  /**
   * How it looks — the id of one of its kind's styles (room3d/openingStyles.ts).
   * Absent = the kind's default. A look only: it changes no quantity.
   */
  style?: string;
  /**
   * Doors and garage doors only: `false` lets this one leave the floor and be
   * placed freely, like a window. Absent = pinned to the floor (the default).
   */
  floorPinned?: boolean;
  /**
   * Floor plans: set on BOTH copies of a window or door that sits in a wall two
   * rooms share — each room has the opening in its own wall, and the plan keeps
   * the two alike. Same value on both.
   */
  link?: string;
  /**
   * A `'passage'` only: set on the copy in the wall that is FRAMED (the older
   * room's), to how thick that wall is (feet). That room finishes the opening
   * through the wall — jambs and head in finished panel, so no framing shows —
   * and the other room's copy, which has none, builds nothing.
   */
  through?: number;
  /**
   * Doors only: which side the hinges are on, as seen from inside THIS room
   * looking at the wall (absent = 'left'), and which way it opens — 'in' into
   * this room, 'out' away from it (absent = 'in'). A door's twin in the room on
   * the other side of a shared wall has both the other way round.
   */
  hinge?: DoorHinge;
  swing?: DoorSwing;
}

export type DoorHinge = 'left' | 'right';
export type DoorSwing = 'in' | 'out';
export const doorHinge = (o: Pick<Opening, 'hinge'>): DoorHinge => o.hinge ?? 'left';
export const doorSwing = (o: Pick<Opening, 'swing'>): DoorSwing => o.swing ?? 'in';
/** The same door seen from the room on the other side of the wall. */
export const mirroredHanding = (o: Pick<Opening, 'hinge' | 'swing'>): { hinge: DoorHinge; swing: DoorSwing } => ({
  hinge: doorHinge(o) === 'left' ? 'right' : 'left',
  swing: doorSwing(o) === 'in' ? 'out' : 'in',
});

/** Doors and garage doors stand on the floor by default; windows never do. */
export function openingSitsOnFloor(kind: OpeningKind): boolean {
  return kind !== 'window';
}

/**
 * Does the safe margin apply to this opening's bottom edge? Not for doors and
 * garage doors, pinned or free: one may always come right down to the floor.
 */
export function openingKeepsOffFloor(opening: OpeningKind | Pick<Opening, 'kind'>): boolean {
  return !openingSitsOnFloor(typeof opening === 'string' ? opening : opening.kind);
}

/** What decides whether one particular opening stands on the floor. */
export type OpeningFloor = Pick<Opening, 'kind' | 'floorPinned'>;

/**
 * Does THIS opening stand on the floor? A door or garage door does unless it
 * has been unpinned. A bare kind is taken at its default.
 */
export function openingOnFloor(opening: OpeningKind | OpeningFloor): boolean {
  if (typeof opening === 'string') return openingSitsOnFloor(opening);
  return openingSitsOnFloor(opening.kind) && opening.floorPinned !== false;
}

/** One editable surface: a wall or the ceiling, with its positioned sections. */
export interface Surface {
  /** Stable id — a wallId, or 'ceiling'. */
  id: string;
  kind: 'wall' | 'ceiling';
  name: string;

  /** Surface extents in feet: width = wall length / ceiling length; height = wall height / ceiling width. */
  width: number;
  height: number;

  /** Positioned sections (see overlap/carving rules). Order is not significant. */
  /**
   * The surface's outline when it is not a plain rectangle (a wall under a
   * sloped ceiling), counter-clockwise from the bottom-left in surface
   * coordinates. Derived from the room's ceiling profile at read time
   * (calculations/plan `roomWallSurfaces`), never stored. Absent = the
   * rectangle `width × height`.
   */
  outline?: [number, number][];
  sections: Section[];

  /** Windows and doors cut through this surface. Walls only. */
  openings?: Opening[];
}

/**
 * The canonical room model. Walls plus an optional ceiling. This is what the
 * wizard generators produce, the 3D editor mutates, and the calc engine reads.
 */
export interface RoomSurfaces {
  /**
   * A ceiling with a profile (calculations/ceiling/profile.ts): one surface per
   * plane, keyed by the plane's id, each with its TRUE size (along the ridge ×
   * up the slope). Present instead of `ceiling` while the room's ceiling is not
   * flat; the plan keeps the two in step (`conformRoomToCeiling`).
   */
  ceilingPlanes?: Record<string, Surface>;
  walls: Surface[];
  ceiling?: Surface;
}

/** Every surface of a room in one list: the walls, then the ceiling (flat, or one surface per plane). */
export function roomSurfaceList(room: RoomSurfaces): Surface[] {
  return [...room.walls, ...(room.ceiling ? [room.ceiling] : []), ...Object.values(room.ceilingPlanes ?? {})];
}

/** The surface with this id, wherever it is in the room. */
export function roomSurfaceById(room: RoomSurfaces, surfaceId: string): Surface | undefined {
  return roomSurfaceList(room).find((s) => s.id === surfaceId);
}

/** The room with `surface` in place of the one with the same id (walls, ceiling or a ceiling plane). */
export function replaceRoomSurface(room: RoomSurfaces, surface: Surface): RoomSurfaces {
  const next: RoomSurfaces = { ...room, walls: room.walls.map((w) => (w.id === surface.id ? surface : w)) };
  if (room.ceiling && room.ceiling.id === surface.id) next.ceiling = surface;
  if (room.ceilingPlanes?.[surface.id]) next.ceilingPlanes = { ...room.ceilingPlanes, [surface.id]: surface };
  return next;
}

/** Helper: is this section a FILL section (carve-able) or an INSERT (carver)? */
export function sectionFillMode(type: SectionProductType): SectionFillMode {
  return type === 'SlatWall' ? 'insert' : 'fill';
}
